import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Scale } from 'lucide-react';
import { api } from '@/lib/api';
import { useMembership } from '@/hooks/useMembership';
import { useDebouncedSignal } from '@/hooks/useDebouncedSignal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useWaitingForConnection } from '@/hooks/useWaitingForConnection';
import {
  NumericInput,
  TextArea,
  AutoSaveIndicator,
  type SaveStatus,
} from '@/components/entry';
import { SamplingResultsCard } from '@/components/entry/SamplingResultsCard';
import { format, parse } from 'date-fns';
import { sameFieldValue, toNumber, toNumberOrNull } from '@/lib/number';
import { todayInPH, parsePhDate, addPhDays } from '@/lib/phDate';
import { qk } from '@/lib/queryKeys';
import {
  ApiError,
  createFailureMessage,
  getRefusedFields,
  registerAutosaveFlusher,
  saveFailureMessage,
} from '@/lib/apiErrors';
import { writeSignal } from '@/lib/writeSignal';
import { useUnsavedEditsWarning } from '@/hooks/useUnsavedEditsWarning';
import type { Sampling } from '@/types/entry';
import type { ApiResponse } from '@/types/api';
import type { PondDetailResponse } from '@/types/generated/openapi';
import { randomId } from '@/lib/randomId';

type TypedField = 'sample_count' | 'total_weight_g' | 'mortality_count' | 'notes';

interface SamplingSearchParams {
  date?: string;
}

export const Route = createFileRoute('/_app/entry/pond/$pondId/sampling')({
  validateSearch: (search: Record<string, unknown>): SamplingSearchParams => {
    return {
      date: typeof search.date === 'string' ? search.date : undefined,
    };
  },
  component: SamplingForm,
});

const FIELD_LABELS: Partial<Record<keyof Sampling, string>> = {
  sample_count: 'Shrimp counted',
  total_weight_g: 'Total Weight',
  mortality_count: 'Dead shrimp',
  notes: 'Notes',
};

// The API accepts 1-1000 shrimp per sample (sample_count gt 0, le 1000).
const MAX_SHRIMP_COUNTED = 1000;
// total_weight_g le 1000000.
const MAX_WEIGHT_G = 1_000_000;

function SamplingForm() {
  const { pondId } = Route.useParams();
  const { date } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { membership, isLoading: membershipLoading } = useMembership();

  const dateStr = date || todayInPH();
  const displayDate = parse(dateStr, 'yyyy-MM-dd', parsePhDate(dateStr));

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<{ message?: string; retryable: boolean } | null>(null);
  const [draftValues, setDraftValues] = useState<Partial<Sampling>>({});
  const pendingChangesRef = useRef<Partial<Sampling>>({});
  const typedRef = useRef<Partial<Record<TypedField, number | string | null>>>({});
  const inFlightRef = useRef(false);
  const creatingRef = useRef(false);

  // Fetch pond data
  const { data: pond, isLoading: pondLoading } = useQuery({
    queryKey: qk.pond.detail(membership?.farmId, pondId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PondDetailResponse>>(
        `/api/v1/farms/${membership?.farmId}/ponds/${pondId}/detail`
      );
      return response.data;
    },
    enabled: !!pondId && !!membership?.farmId,
  });

  const cycleId = pond?.active_cycle?.id;
  const stockingCount = toNumber(pond?.active_cycle?.stocking_count, 0);
  const samplingKey = ['sampling', cycleId, dateStr] as const;

  // Fetch sampling for the day
  const { data: sampling, isLoading: samplingLoading } = useQuery({
    queryKey: samplingKey,
    queryFn: async () => {
      const response = await api.get<{ data: Sampling[] }>(
        `/api/v1/farms/${membership?.farmId}/samplings`,
        {
          params: {
            cycle_id: cycleId!,
            start_date: dateStr,
            end_date: dateStr,
          },
        }
      );
      const samplings = response.data || [];
      return samplings.find((s) => s.sampling_date === dateStr) || samplings[0] || null;
    },
    enabled: !!cycleId && !!membership?.farmId,
  });

  // Issue #112: fetch only the single most-recent sampling strictly before the
  // current date. Previous implementation paginated the entire cycle history
  // just to take the first element; for a 120-day cycle with weekly sampling
  // that's 17 rows loaded every page view.
  const { data: previousSampling } = useQuery({
    queryKey: qk.samplings.latestBefore(membership?.farmId, cycleId || '', dateStr),
    queryFn: async () => {
      const response = await api.get<{ data: Sampling[] }>(
        `/api/v1/farms/${membership?.farmId}/samplings`,
        {
          params: {
            cycle_id: cycleId!,
            end_date: addPhDays(dateStr, -1),
            limit: '1',
            sort: 'desc',
          },
        }
      );
      return response.data?.[0] || null;
    },
    enabled: !!cycleId && !!membership?.farmId,
  });

  // Not the row id: it goes undefined -> optimistic -> real while a first save is in flight,
  // which would wipe the edits typed meanwhile.
  const resetKey = `${dateStr}-${cycleId}`;
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setDraftValues({});
  }
  useEffect(() => {
    pendingChangesRef.current = {};
    typedRef.current = {};
  }, [resetKey]);

  // The API stores 0 when a sampling is created without a mortality count. That 0 was never
  // entered, so until the user types one it reads as blank (survival and biomass show "—").
  const [defaultedMortalityKey, setDefaultedMortalityKey] = useState<string | null>(null);
  const mortalityDefaulted =
    defaultedMortalityKey === resetKey && !(toNumber(sampling?.mortality_count, 0) > 0);
  const mortalityShown =
    draftValues.mortality_count ?? (mortalityDefaulted ? null : sampling?.mortality_count);

  // Calculate metrics
  const calculations = useMemo(() => {
    const sampleCount = toNumber(draftValues.sample_count ?? sampling?.sample_count, 0);
    const totalWeight = toNumber(draftValues.total_weight_g ?? sampling?.total_weight_g, 0);
    const mortalityCount = mortalityShown == null ? null : toNumber(mortalityShown, 0);

    // ABW = total weight / sample count, to 2 decimals half-up: the stored abw_g column is
    // numeric(6,2) and the server's biomass uses that rounded value, so Overview and the PDF
    // show the same ABW and biomass as this preview (0.955 g -> 0.96 g, not JS's 0.95).
    const abw = sampleCount > 0 ? Math.floor((totalWeight / sampleCount) * 100 + 0.5 + 1e-9) / 100 : null;

    // Survival and biomass both need the dead-shrimp count; a blank one is not "no deaths".
    const survivalCount = mortalityCount === null ? null : stockingCount - mortalityCount;
    const survivalRate =
      survivalCount !== null && stockingCount > 0 ? (survivalCount / stockingCount) * 100 : null;

    // Biomass = ABW × survival count / 1000 (convert to kg)
    const biomass =
      abw !== null && survivalCount !== null && survivalCount > 0
        ? (abw * survivalCount) / 1000
        : null;

    return {
      abw,
      biomass,
      survivalRate,
      previousAbw: toNumberOrNull(previousSampling?.abw_g),
    };
  }, [draftValues, sampling, mortalityShown, stockingCount, previousSampling]);

  const waitingForConnection = useWaitingForConnection();

  const farmId = membership?.farmId;
  const invalidateSamplingViews = () => {
    queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
    queryClient.invalidateQueries({ queryKey: ['samplings', farmId] });
    queryClient.invalidateQueries({ queryKey: qk.pond.entryStatus(pondId, dateStr) });
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'entry-checklist' && q.queryKey[2] === farmId,
    });
  };

  // Save mutation
  const saveMutation = useMutation({
    // POST isn't idempotent: a retry after a lost response would 409 on the row it just made.
    retry: (failureCount, error) => {
      if (creatingRef.current) return false;
      const status = (error as ApiError | undefined)?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
    // The target is decided when the save is sent: onMutate puts an optimistic row in the
    // cache and a re-render would otherwise make a create look like an update of it.
    mutationFn: async ({ changes, id }: { changes: Partial<Sampling>; id: string | null }) => {
      if (id) {
        return api.put(`/api/v1/farms/${membership?.farmId}/samplings/${id}`, changes, {
          signal: writeSignal(),
        });
      }
      return api.post(
        `/api/v1/farms/${membership?.farmId}/samplings`,
        {
          cycle_id: cycleId,
          sampling_date: dateStr,
          ...Object.fromEntries(Object.entries(changes).filter(([, v]) => v !== null)),
        },
        { signal: writeSignal() }
      );
    },
    onMutate: async ({ changes }) => {
      setSaveStatus('saving');
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: samplingKey });
      const previous = queryClient.getQueryData<Sampling | null>(samplingKey);
      const nowIso = new Date().toISOString();
      const base: Sampling = previous ?? {
        id: `optimistic-${randomId()}`,
        farm_id: membership?.farmId,
        cycle_id: cycleId,
        sampling_date: dateStr,
        sample_count: 1,
        total_weight_g: 0,
        mortality_count: null,
        notes: null,
        data_source: 'manual',
        technician_id: null,
        abw_g: null,
        estimated_pieces_alive: null,
        survival_rate_pct: null,
        estimated_biomass_kg: null,
        created_at: nowIso,
        last_modified: nowIso,
        deleted_at: null,
      };

      const next = {
        ...base,
        ...changes,
        last_modified: nowIso,
      } as Sampling;

      queryClient.setQueryData(samplingKey, next);
      return { previous };
    },
    onSuccess: (result, vars) => {
      setSaveStatus('saved');
      setSaveError(null);
      if (!vars.id && vars.changes.mortality_count == null) setDefaultedMortalityKey(resetKey);
      invalidateSamplingViews();
      const updated = (result as { data?: Sampling })?.data ?? (result as Sampling | undefined);
      if (updated) {
        queryClient.setQueryData(samplingKey, updated);
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (err, vars, context) => {
      // The edits were drained from the buffer before the send; put them back so the next
      // blur, the retry button or leaving the page sends them again. A 400/422 means the server
      // read those values and refused them: sending them again only fails again, and would
      // block every other edit riding in the same PUT. Edits typed since the send win.
      const refused =
        err instanceof ApiError && (err.status === 400 || err.status === 422) ? getRefusedFields(err) : null;
      const named = refused?.map((r) => r.field).filter((f) => f in vars.changes) ?? [];
      // A refusal that names nothing on an update would repeat forever; on a create the typed
      // values are all the user has, so they stay.
      const dropped: string[] = refused ? (named.length ? named : vars.id ? Object.keys(vars.changes) : []) : [];
      const requeue: Partial<Sampling> = { ...vars.changes };
      dropped.forEach((f) => delete requeue[f as keyof Sampling]);
      pendingChangesRef.current = { ...requeue, ...pendingChangesRef.current };
      if (dropped.length) {
        setDraftValues((prev) => {
          const next = { ...prev };
          dropped.forEach((f) => {
            if (!(f in pendingChangesRef.current)) delete next[f as keyof Sampling];
          });
          return next;
        });
      }
      const refusedMessage = refused?.length
        ? refused
            .filter((r) => r.field in vars.changes)
            .map((r) => `${FIELD_LABELS[r.field as keyof Sampling] ?? r.field} ${r.message ?? "isn't accepted"}`)
            .join('; ')
        : '';
      setSaveError({
        message: refusedMessage || undefined,
        retryable: Object.keys(pendingChangesRef.current).length > 0,
      });
      setSaveStatus('error');
      if (context?.previous !== undefined) {
        queryClient.setQueryData(samplingKey, context.previous);
      }
      // 409 (someone else made today's row), 5xx, or no response at all (a lost response
      // may still have created it): refetch so the next save is a PUT, not another POST.
      const createOutcomeUnknown =
        !vars.id && (!(err instanceof ApiError) || err.status === 409 || err.status >= 500);
      if (createOutcomeUnknown) {
        queryClient.invalidateQueries({ queryKey: samplingKey });
      }
      // Issue #118: the AutoSaveIndicator is easy to miss.
      const msg = refusedMessage || (vars.id
        ? saveFailureMessage(err, 'Unable to save sampling')
        : createFailureMessage(err, 'Unable to save sampling'));
      toast.error(msg, { id: 'sampling-save-error' });
    },
    // Edits that arrived mid-save are sent here, not from an effect: this still runs when
    // the route was left while the save was in flight.
    onSettled: (_data, err) => {
      inFlightRef.current = false;
      if (!err) flushPendingRef.current();
    },
  });

  const flushPending = () => {
    if (inFlightRef.current) return;
    const changes = pendingChangesRef.current;
    if (!cycleId || !Object.keys(changes).length) return;
    const id = queryClient.getQueryData<Sampling | null>(samplingKey)?.id ?? null;
    if (id?.startsWith('optimistic-')) return;
    if (!id) {
      // Issue #113: require both sample_count and total_weight before creating, or a lone
      // mortality_count would make a row with ABW = 0 that pollutes the cycle metrics.
      // The edits stay buffered until the other core field arrives.
      const complete = toNumber(changes.sample_count, 0) > 0 && toNumber(changes.total_weight_g, 0) > 0;
      if (!complete) {
        setSaveStatus('idle');
        if ('mortality_count' in changes || 'notes' in changes) {
          toast.error('Enter shrimp counted and total weight before saving other fields.', {
            id: 'sampling-save-error',
          });
        }
        return;
      }
    }
    pendingChangesRef.current = {};
    inFlightRef.current = true;
    creatingRef.current = !id;
    saveMutation.mutate({ changes, id });
  };

  const flushPendingRef = useRef(flushPending);
  useEffect(() => {
    flushPendingRef.current = flushPending;
  });

  // A create started by an earlier mount of this page (left and came back while it was in
  // flight) lands here as the row id going optimistic -> real; send what was typed meanwhile.
  const sampleId = sampling?.id;
  const prevSampleIdRef = useRef(sampleId);
  useEffect(() => {
    const prev = prevSampleIdRef.current;
    prevSampleIdRef.current = sampleId;
    if (prev?.startsWith('optimistic-') && sampleId && !sampleId.startsWith('optimistic-')) {
      flushPendingRef.current();
    }
  }, [sampleId]);

  const { signal: scheduleSave, flush: flushNow } = useDebouncedSignal(flushPending, {
    delayMs: 600,
    flushOnHide: true,
  });

  // Issue #86: flush buffered changes on 401 so session expiry doesn't silently
  // drop edits. No deps: flushAllPendingAutosaves empties the registry, so register
  // again on every render.
  useEffect(() => registerAutosaveFlusher(flushNow));

  const queueFieldSave = useCallback(
    (field: keyof Sampling, value: unknown) => {
      const currentValue =
        field === 'mortality_count' && mortalityDefaulted ? null : sampling?.[field];

      // A saved row cannot lose its count or weight; a cleared box that got here (typed while
      // the row did not exist yet) is dropped, not queued as a null the server refuses.
      if (sampling?.id && value === null && (field === 'sample_count' || field === 'total_weight_g')) {
        delete pendingChangesRef.current[field];
        setDraftValues((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
        return;
      }

      setDraftValues((prev) => ({
        ...prev,
        [field]: value as Sampling[keyof Sampling],
      }));

      if (sameFieldValue(value, currentValue)) {
        if (field in pendingChangesRef.current) {
          const next = { ...pendingChangesRef.current };
          delete next[field];
          pendingChangesRef.current = next;
        }
        return;
      }

      pendingChangesRef.current = {
        ...pendingChangesRef.current,
        [field]: value as Sampling[keyof Sampling],
      };
      setSaveStatus('saving');
      scheduleSave();
    },
    [sampling, mortalityDefaulted, scheduleSave]
  );

  // The debounce is cancelled on unmount and the reset effect wipes the buffer on a
  // date/cycle change, so edits made <600 ms before leaving would be dropped, and a focused
  // field never blurs when its route is torn down, so what it holds is committed here.
  // Cleanup runs before the next render's effects: last committed closure, old key, buffer
  // still intact.
  const leaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    leaveRef.current = () => {
      const typed = typedRef.current;
      typedRef.current = {};
      (Object.keys(typed) as TypedField[]).forEach((field) => queueFieldSave(field, typed[field]));
      // flushNow also cancels the debounce that queueFieldSave just armed, which would
      // otherwise fire after the page is gone.
      flushNow();
      const hasRow = !!queryClient.getQueryData<Sampling | null>(samplingKey)?.id;
      if (!hasRow && !inFlightRef.current && Object.keys(pendingChangesRef.current).length > 0) {
        toast.error('Not saved: enter shrimp counted and total weight', { id: 'sampling-save-error' });
      }
    };
  });
  useEffect(() => () => leaveRef.current(), [resetKey]);

  useUnsavedEditsWarning(
    () =>
      Object.keys(typedRef.current).length > 0 ||
      Object.keys(pendingChangesRef.current).length > 0 ||
      inFlightRef.current
  );

  const commitField = (field: TypedField, value: unknown) => {
    delete typedRef.current[field];
    queueFieldSave(field, value);
    flushNow();
  };

  if (membershipLoading || pondLoading || samplingLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={32} />
      </div>
    );
  }


  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-30 -my-2 py-2 bg-gray-50 flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate({ to: `/entry/pond/${pondId}`, search: { date: dateStr } })}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50">
                <Scale className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{pond?.name}</h1>
                <p className="text-sm text-gray-500">
                  Sampling • {format(displayDate, 'EEEE, MMMM d')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {waitingForConnection ? (
            <span role="status" className="text-sm font-medium text-amber-800 text-right">
              No connection, will save when back online
            </span>
          ) : !cycleId ? null : saveStatus === 'idle' ? (
            <span className="text-xs text-gray-500">Saves automatically</span>
          ) : (
            <AutoSaveIndicator
              status={saveStatus}
              message={saveStatus === 'error' ? saveError?.message : undefined}
              onRetry={saveStatus === 'error' && !saveError?.retryable ? undefined : flushNow}
            />
          )}
        </div>
      </div>

      {!cycleId ? (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">
            This pond has no active crop, so sampling can't be logged.
          </p>
        </div>
      ) : (
        <>
          {/* Calculated Results */}
          <SamplingResultsCard
            abw={calculations.abw}
            biomass={calculations.biomass}
            survivalRate={calculations.survivalRate}
            previousAbw={calculations.previousAbw}
          />

          {/* Form Fields */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <h2 className="font-semibold text-gray-900">Sample Data</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                How many shrimp you weighed, and what they weighed together
              </p>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <NumericInput
                  label="Shrimp counted"
                  integer
                  example="100"
                  required={!!sampling?.id}
                  value={toNumberOrNull(draftValues.sample_count ?? sampling?.sample_count)}
                  min={1}
                  step={1}
                  validate={(n) =>
                    n < 1
                      ? 'Count at least 1 shrimp'
                      : n > MAX_SHRIMP_COUNTED
                        ? `Shrimp counted can't be more than ${MAX_SHRIMP_COUNTED}`
                        : null
                  }
                  onValueChange={(value) => {
                    typedRef.current.sample_count = value;
                  }}
                  onRefusalChange={(refused) => {
                    if (refused) delete typedRef.current.sample_count;
                  }}
                  onBlur={(value) => commitField('sample_count', value)}
                />
                <NumericInput
                  label="Total Weight"
                  unit="g"
                  required={!!sampling?.id}
                  value={toNumberOrNull(draftValues.total_weight_g ?? sampling?.total_weight_g)}
                  min={0}
                  step={0.1}
                  validate={(n) =>
                    n <= 0
                      ? 'Weight must be more than 0'
                      : n > MAX_WEIGHT_G
                        ? `Total weight can't be more than ${MAX_WEIGHT_G.toLocaleString()} g`
                        : null
                  }
                  onValueChange={(value) => {
                    typedRef.current.total_weight_g = value;
                  }}
                  onRefusalChange={(refused) => {
                    if (refused) delete typedRef.current.total_weight_g;
                  }}
                  onBlur={(value) => commitField('total_weight_g', value)}
                />
                <NumericInput
                  label="Dead shrimp since stocking (total)"
                  integer
                  example="100"
                  value={toNumberOrNull(mortalityShown)}
                  min={0}
                  step={1}
                  validate={(n) =>
                    stockingCount > 0 && n > stockingCount
                      ? `Dead shrimp can't be more than the ${stockingCount.toLocaleString()} stocked`
                      : null
                  }
                  placeholder="Enter to see survival and biomass"
                  onValueChange={(value) => {
                    typedRef.current.mortality_count = value;
                  }}
                  onBlur={(value) => commitField('mortality_count', value)}
                />
                <div className="flex items-end">
                  <div className="bg-gray-50 rounded-lg p-4 w-full">
                    <p className="text-xs text-gray-500 mb-1">Stocking Count</p>
                    <p className="text-xl font-semibold text-gray-900 tabular-nums">
                      {stockingCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {!sampling?.id &&
                (toNumber(draftValues.sample_count, 0) > 0) !== (toNumber(draftValues.total_weight_g, 0) > 0) && (
                  <p role="status" className="mt-4 text-sm text-amber-800">
                    Not saved yet. Enter both shrimp counted and total weight to save.
                  </p>
                )}

              <div className="mt-5">
                <TextArea
                  label="Notes"
                  value={(draftValues.notes ?? sampling?.notes) || ''}
                  onValueChange={(value) => {
                    typedRef.current.notes = value;
                  }}
                  onBlur={(value) => commitField('notes', value)}
                  rows={3}
                  placeholder="Observations about shrimp health, behavior, etc."
                />
              </div>
            </div>
          </div>

          {/* Calculation Explanation */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
            <h3 className="font-medium text-gray-900 mb-3">How calculations work</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong className="text-gray-700">ABW</strong> = Total Weight ÷ Shrimp counted
              </p>
              <p>
                <strong className="text-gray-700">Survival Rate</strong> = (Stocking − Dead shrimp since stocking) ÷ Stocking × 100
              </p>
              <p>
                <strong className="text-gray-700">Biomass</strong> = ABW × Surviving Count ÷ 1000
              </p>
              <p className="text-gray-500">
                Survival and Biomass show — until the dead-shrimp count is entered.
              </p>
            </div>
          </div>

        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <Link
          to="/entry/pond/$pondId"
          params={{ pondId }}
          search={{ date: dateStr }}
          className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          ← Back to entry hub
        </Link>
      </div>
    </div>
  );
}
